import { Inviter, Registerer, SessionState, UserAgent } from 'sip.js'
import type { Invitation, Session as SipSession } from 'sip.js'
import type { SipCredentials } from './types'

/**
 * The browser softphone.
 *
 * The agent registers one WebRTC endpoint and then answers exactly one call:
 * the persistent agent leg the dialer originates at login, which stays up for
 * the whole shift. Customers are bridged into that leg server-side, so from the
 * browser's point of view nothing happens between calls -- there is no second
 * INVITE, no renegotiation, and no place for the browser to add latency to the
 * connect.
 *
 * That is the reason this file is short. The complexity that a softphone
 * normally carries lives in the dialer instead, where it can be tested.
 */
export class Softphone {
  private ua: UserAgent | null = null
  private registerer: Registerer | null = null
  private session: SipSession | null = null

  constructor(
    private readonly audioElement: HTMLAudioElement,
    private readonly onStatus: (status: SoftphoneStatus, detail?: string) => void,
  ) {}

  async connect(creds: SipCredentials): Promise<void> {
    const uri = UserAgent.makeURI(`sip:${creds.username}@${hostFromWs(creds.ws_uri)}`)
    if (!uri) throw new Error(`could not build a SIP URI for ${creds.username}`)

    this.ua = new UserAgent({
      uri,
      transportOptions: { server: creds.ws_uri },
      authorizationUsername: creds.username,
      authorizationPassword: creds.password,
      // The agent leg is inbound only. Auto-answering it is correct and is what
      // makes login a single click rather than a click plus an answer.
      delegate: {
        onInvite: (invitation: Invitation) => void this.answer(invitation),
        onDisconnect: (error) => {
          this.onStatus('disconnected', error?.message)
        },
      },
      // Agents sit idle for long stretches; without this the transport is torn
      // down and the next call rings a phone that is no longer registered.
      logBuiltinEnabled: false,
    })

    this.onStatus('connecting')
    await this.ua.start()

    this.registerer = new Registerer(this.ua, { expires: 300 })
    this.registerer.stateChange.addListener((state) => {
      if (state === 'Registered') this.onStatus('registered')
      if (state === 'Unregistered') this.onStatus('unregistered')
    })
    await this.registerer.register()
  }

  private async answer(invitation: Invitation): Promise<void> {
    this.session = invitation

    invitation.stateChange.addListener((state) => {
      if (state === SessionState.Established) {
        this.attachRemoteAudio(invitation)
        this.onStatus('on_leg')
      }
      if (state === SessionState.Terminated) {
        this.session = null
        this.audioElement.srcObject = null
        this.onStatus('registered')
      }
    })

    await invitation.accept({
      sessionDescriptionHandlerOptions: {
        // Microphone only. Asking for video would prompt for camera permission
        // an agent has no use for, and one denied prompt breaks the call.
        constraints: { audio: true, video: false },
      },
    })
  }

  /**
   * Pipe the remote track to the audio element.
   *
   * Building a fresh MediaStream from the receivers rather than using the peer
   * connection's remote streams directly: Asterisk renegotiates the session
   * when a customer is bridged in, and the stream identity is not guaranteed
   * to survive that. The tracks are.
   */
  private attachRemoteAudio(session: SipSession): void {
    const sdh = session.sessionDescriptionHandler as
      | { peerConnection?: RTCPeerConnection }
      | undefined
    const pc = sdh?.peerConnection
    if (!pc) return

    const stream = new MediaStream()
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track) stream.addTrack(receiver.track)
    })
    this.audioElement.srcObject = stream
    void this.audioElement.play().catch(() => {
      // Autoplay was blocked. The agent has to interact with the page once
      // before audio flows; the UI surfaces this rather than leaving them on a
      // silent call wondering why nobody is talking.
      this.onStatus('audio_blocked')
    })
  }

  /** Mute the agent's microphone without touching the call. */
  setMuted(muted: boolean): void {
    const sdh = this.session?.sessionDescriptionHandler as
      | { peerConnection?: RTCPeerConnection }
      | undefined
    sdh?.peerConnection?.getSenders().forEach((sender) => {
      if (sender.track?.kind === 'audio') sender.track.enabled = !muted
    })
  }

  /** Send a DTMF digit down the live leg, for IVRs and extension transfers. */
  sendDtmf(digit: string): void {
    const sdh = this.session?.sessionDescriptionHandler as
      | { sendDtmf?: (tone: string) => boolean }
      | undefined
    sdh?.sendDtmf?.(digit)
  }

  async disconnect(): Promise<void> {
    try {
      if (this.session && this.session.state === SessionState.Established) {
        await (this.session as Inviter).bye?.()
      }
      await this.registerer?.unregister()
      await this.ua?.stop()
    } finally {
      this.session = null
      this.registerer = null
      this.ua = null
      this.audioElement.srcObject = null
      this.onStatus('disconnected')
    }
  }
}

export type SoftphoneStatus =
  | 'idle'
  | 'connecting'
  | 'registered'
  | 'unregistered'
  | 'on_leg'
  | 'audio_blocked'
  | 'disconnected'

function hostFromWs(wsUri: string): string {
  try {
    return new URL(wsUri).hostname
  } catch {
    return wsUri
  }
}
